import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { PanelGroup, Panel, PanelResizeHandle } from "react-resizable-panels";
import { runCode } from "../utils/api";
import LanguageSelector from "./coding_app_components/LanguageSelector";
import CodeEditor from "./coding_app_components/CodeEditor";
import InputOutput from "./coding_app_components/InputOutput";
import Question from "./coding_app_components/Question";
import FileTabs from "./coding_app_components/FileTabs";
import { defaultTemplates } from "../utils/codeTemplates";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import { useNotification } from "../NotificationContext";
import { useUIVirtualFS } from "./coding_app_components/useUIVirtualFS";


// Language mapping function
const normalizeLanguage = (lang) => {
  if (!lang) return "java"; // default

  const langMap = {
    // Lowercase
    'c': 'c',
    'cpp': 'cpp',
    'c++': 'cpp',
    'java': 'java',
    'python': 'python',
    'javascript': 'javascript',
    'js': 'javascript',
    'react': 'react',
    'angular': 'angular',
    'perl': 'perl',
    'go': 'go',
    'sql': 'sql',

    // Capitalized
    'C': 'c',
    'Cpp': 'cpp',
    'C++': 'cpp',
    'Java': 'java',
    'Python': 'python',
    'Javascript': 'javascript',
    'JavaScript': 'javascript',
    'JS': 'javascript',
    'React': 'react',
    'Angular': 'angular',
    'Perl': 'perl',
    'Go': 'go',
    'Sql': 'sql',
    'SQL': 'sql',

    // Full names
    'C Language': 'c',
    'C++ Language': 'cpp',
    'Java Programming': 'java',
    'Python Programming': 'python',
    'JavaScript Programming': 'javascript',
    'React Programming': 'react',
    'Angular Programming': 'angular',
    'Perl Programming': 'perl',
    'Golang': 'go',
    'SQL Programming': 'sql'
  };

  return langMap[lang] || "java"; // default to java if not found
};


import { useTheme } from "../../theme/ThemeContext";

const CodingApp = forwardRef(({
  interviewId,
  questions = [],
  currentQuestionIndex = 0,
  onQuestionIndexChange = () => {},
  totalTimeLimit = 0,
  panelVisible,
  endInterview,
}, ref) => {
  const { currentTheme } = useTheme();
  const darkTheme = currentTheme === 'dark';
  const { notify } = useNotification();
  const [activeTab, setActiveTab] = useState("input");
  const [language, setLanguage] = useState("java");
  const [codeByQuestion, setCodeByQuestion] = useState({});
  const [input, setInput] = useState("");
  const [output, setOutput] = useState(null);
  // Remove local darkTheme state
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [submittedQuestions, setSubmittedQuestions] = useState({});
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Tracks when each question was shown to the candidate (key = questionKey)
  const askedAtRef = useRef({});

  // Get current question from array
  const currentQuestion = questions[currentQuestionIndex] || null;

  // Create stable key for storing per-question code
  const questionKey =
    currentQuestion?._id ||
    currentQuestion?.questionId ||
    currentQuestionIndex;

  // Clear input & output whenever question changes
  useEffect(() => {
    setInput("");
    setOutput(null);
  }, [questionKey]);

  // Get the correct code for the current question
  const currentCode =
    codeByQuestion[questionKey] ??
    defaultTemplates[language] ??
    "";

  // Check if current language is UI mode (React/Angular)
  const isUIMode = language === 'react' || language === 'angular';

  // Virtual file system for React/Angular (only initialized when needed)
  const virtualFS = useUIVirtualFS(isUIMode ? language : 'react');

  const auto_generate = false;

  // Timer state for coding questions (shared across all questions)
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [timeUp, setTimeUp] = useState(false);
  const timerRef = useRef(null);

  // When panel becomes visible and there are questions, initialize timer with totalTimeLimit
  useEffect(() => {
    console.log("Timer effect:", { panelVisible, questionsLength: questions.length, totalTimeLimit, timeRemaining });

    if (panelVisible && questions.length > 0 && totalTimeLimit > 0) {
      const limit = parseInt(totalTimeLimit || 0, 10);

      // Set language from current question (with normalization)
      if (currentQuestion && currentQuestion.language) {
        const normalizedLang = normalizeLanguage(currentQuestion.language);
        setLanguage(normalizedLang);
      }

      // Initialize timer with totalTimeLimit (only once at the beginning)
      if (timeRemaining === 0 && limit > 0) {
        console.log("Initializing timer with limit:", limit);
        setTimeRemaining(limit);
        setTimeUp(false);
        setTimerActive(true);
      }
    } else if (!panelVisible) {
      setTimerActive(false);
      setTimeRemaining(0);
      setTimeUp(false);
      // Clear asked_at tracking when panel closes
      askedAtRef.current = {};
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [panelVisible, questions.length, totalTimeLimit]);

  // Record asked_at when a question becomes active (panel visible + question present)
  useEffect(() => {
    if (panelVisible && currentQuestion && !askedAtRef.current[questionKey]) {
      askedAtRef.current[questionKey] = new Date().toISOString();
    }
  }, [panelVisible, questionKey, currentQuestion]);

  // Update language when question index changes (but don't reset timer)
  useEffect(() => {
    if (currentQuestion && currentQuestion.language) {
      const normalizedLang = normalizeLanguage(currentQuestion.language);
      setLanguage(normalizedLang);
    }
  }, [currentQuestionIndex, currentQuestion]);

  // countdown effect
  useEffect(() => {
    if (timerActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((t) => {
          if (t <= 1) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            setTimerActive(false);
            setTimeUp(true);
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [timerActive, timeRemaining]);

 // Set code template when language changes OR when question changes (but preserve user code)
  useEffect(() => {
    if (!currentQuestion) return;

    if (isUIMode) {
      // For UI mode, reset virtual file system
      virtualFS.resetFiles();
      // Set to preview tab for UI mode
      setActiveTab("preview");
    } else {
      // For regular mode, use templates
      setCodeByQuestion((prev) => {
        // If user already typed for this question, DO NOTHING
        if (prev[questionKey]) return prev;

        // Otherwise load template ONCE
        const normalizedLang = currentQuestion.language
          ? normalizeLanguage(currentQuestion.language)
          : language;
        return {
          ...prev,
          [questionKey]: defaultTemplates[normalizedLang] || "",
        };
      });
      // Set to input tab for non-UI mode
      setActiveTab("input");
    }
  }, [language, questionKey, isUIMode, currentQuestion]);

   // Handle language change - but disable if time is up
  const handleLanguageChange = (newLanguage) => {
    if (timeUp) {
      notify("error", "Time is up. You cannot change language.");
      return;
    }
    setLanguage(newLanguage);
  };

  // Navigation checker - shows confirmation if question not submitted
  const tryNavigate = (direction) => {
    if (submittedQuestions[questionKey]) {
      if (direction === "next") {
        onQuestionIndexChange(currentQuestionIndex + 1);
      } else if (direction === "prev") {
        onQuestionIndexChange(currentQuestionIndex - 1);
      }
      return;
    }
    setPendingNavigation(direction);
    setShowConfirmPopup(true);
  };

  // Handle next question
  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      tryNavigate("next");
    }
  };

  // Handle previous question
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      tryNavigate("prev");
    }
  };

  const handleRun = async () => {
    if (timeUp) {
      notify("error", "Time is up. You cannot run code.");
      return;
    }

    if (isUIMode) {
      // For React/Angular, trigger preview update
      setIsPreviewLoading(true);
      setActiveTab("preview");
      // Debounce delay for preview to render
      setTimeout(() => {
        setIsPreviewLoading(false);
      }, 500);
      notify("success", "Preview updated!");
    } else {
      // For other languages, call backend
      setActiveTab("output");
      setOutput("Compiling");
      const result = await runCode({ language,  code: currentCode, auto_generate, input });
      setOutput(result);
    }
  };

  const submitSingleQuestion = async (question, index) => {
    const key =
      question?._id ||
      question?.questionId ||
      index;
  
    const questionLanguage = normalizeLanguage(
      question?.language || "java"
    );
  
    let answerContent = "";
  
    // UI mode
    if (
      questionLanguage === "react" ||
      questionLanguage === "angular"
    ) {
      // UI mode can only safely submit the currently loaded virtualFS.
      // For normal coding languages, codeByQuestion is used below.
      if (key === questionKey) {
        if (questionLanguage === "react") {
          answerContent = virtualFS.files["App.jsx"] || "";
        } else {
          answerContent =
            virtualFS.files["app.component.ts"] || "";
        }
      }
    } else {
      answerContent = codeByQuestion[key] || "";
    }
  
    const payload = {
      interview_id:
        interviewId ||
        localStorage.getItem("interviewId") ||
        "",
  
      question:
        question?.questionText ||
        question?.description ||
        "code submission",
  
      answer: answerContent,
  
      code: answerContent,
  
      language: questionLanguage,
  
      question_id:
        question?._id ||
        question?.questionId ||
        "",
  
      asked_at:
        askedAtRef.current[key] ||
        null,
    };
  
    const res = await fetch(
      `${process.env.REACT_APP_REST_BASE_URL}/interview_ai/interview/submit-code`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      }
    );
  
    if (!res.ok) {
      const text = await res.text();
  
      throw new Error(
        `Question ${index + 1} submission failed: ${res.status} ${text}`
      );
    }
  
    return await res.json();
  };
  
  
  const submittingAllRef = useRef(false);
  
  const submitAllCodingAnswers = async () => {
    if (submittingAllRef.current) {
      return;
    }
  
    submittingAllRef.current = true;
  
    try {
      let submittedCount = 0;
  
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
      
        const key =
          question?._id ||
          question?.questionId ||
          i;
      
        if (submittedQuestions[key]) {
          console.log(
            `⏭️ Coding question ${i + 1} already submitted`
          );
          continue;
        }
      
        await submitSingleQuestion(question, i);
      
        submittedCount++;
      
        console.log(
          `✅ Coding question ${i + 1}/${questions.length} submitted`
        );
      }
  
      setSubmittedQuestions((prev) => {
        const updated = { ...prev };
  
        questions.forEach((question, index) => {
          const key =
            question?._id ||
            question?.questionId ||
            index;
  
          updated[key] = true;
        });
  
        return updated;
      });
  
      return submittedCount;
    } finally {
      submittingAllRef.current = false;
    }
  };
  
  
  const handleSubmit = async () => {
    try {
      const submittedCount = await submitSingleQuestion(
        currentQuestion,
        currentQuestionIndex
      );
  
      notify(
        "success",
        submittedCount?.message ||
          "Code submitted successfully"
      );
  
      setInput("");
  
      setSubmittedQuestions((prev) => ({
        ...prev,
        [questionKey]: true,
      }));
    } catch (e) {
      console.error("Submit error", e);
  
      notify(
        "error",
        "Submit error: " + e.message
      );
    }
  };
  // Disable copy/paste inside the CodeEditor only
  const codeEditorRef = useRef(null);
  useEffect(() => {
    const editorArea = codeEditorRef.current;
    if (!editorArea) return;
    const handleCopyPaste = (e) => {
      e.preventDefault();
      notify(
        "warning",
        "Copy & Paste are disabled in the code editor during this test."
      );
    };
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        ["c", "v", "x", "a"].includes(e.key.toLowerCase())
      ) {
        e.preventDefault();
        notify(
          "warning",
          "Copy/Paste/Cut/Select All are disabled in the code editor."
        );
      }
    };
    // Attach listeners
    editorArea.addEventListener("copy", handleCopyPaste);
    editorArea.addEventListener("paste", handleCopyPaste);
    editorArea.addEventListener("cut", handleCopyPaste);
    editorArea.addEventListener("contextmenu", handleCopyPaste);
    editorArea.addEventListener("drop", handleCopyPaste);
    editorArea.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      editorArea.removeEventListener("copy", handleCopyPaste);
      editorArea.removeEventListener("paste", handleCopyPaste);
      editorArea.removeEventListener("cut", handleCopyPaste);
      editorArea.removeEventListener("contextmenu", handleCopyPaste);
      editorArea.removeEventListener("drop", handleCopyPaste);
      editorArea.removeEventListener("keydown", handleKeyDown);
    };
  }, [notify]);
  useImperativeHandle(ref, () => ({
    submitAllCodingAnswers,
  }));

  return (
    <div className={`${darkTheme ? "dark" : ""}`}>
      <div className="flex flex-col h-screen bg-gradient-to-b from-[#F7F4FE] via-[#F6F3FD] to-[#F2EDFD] text-[#191F2D] dark:bg-none dark:bg-gray-900 dark:text-white transition-colors duration-300">
        <Header
          handleRun={handleRun}
          handleSubmit={handleSubmit}
          runDisabled={timeUp}
          timeRemaining={timeRemaining} // 👈 Add this
          timeUp={timeUp} // 👈 Add this
        />
        <div className="flex-1 overflow-hidden bg-gradient-to-b from-[#F8F6FE] to-[#F4F0FD] dark:from-slate-900 dark:to-slate-950">
          <PanelGroup direction="horizontal">
            {/* Left Panel (Question) */}
            <Panel defaultSize={35}>
              <div className="h-full px-2 py-1">
                <div className="h-full flex flex-col rounded-lg border border-[#E6E0F3] bg-white/75 dark:border-slate-700 dark:bg-slate-800/90 shadow-sm px-3 py-2">
                  {/* Header with Title and Navigation Buttons */}
                  <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
                    <h4 className="text-sm font-semibold text-[#7A5BC7] dark:text-violet-300">
                      Question {currentQuestionIndex + 1} of {questions.length}
                    </h4>
                    {questions.length > 1 && (
                      <div className="flex gap-2 items-center">
                        <button
                          onClick={handlePreviousQuestion}
                          disabled={currentQuestionIndex === 0}
                          className={`px-2 py-1 text-xs font-semibold rounded transition-colors
                            ${
                              currentQuestionIndex === 0
                                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                                : 'bg-[#4B1498] text-white hover:bg-[#3B0F7A] active:bg-[#35106D]'
                            }`}
                        >
                          ← Prev
                        </button>
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {currentQuestionIndex + 1}/{questions.length}
                        </span>
                        <button
                          onClick={handleNextQuestion}
                          disabled={currentQuestionIndex === questions.length - 1}
                          className={`px-2 py-1 text-xs font-semibold rounded transition-colors
                            ${
                              currentQuestionIndex === questions.length - 1
                                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                                : 'bg-[#4B1498] text-white hover:bg-[#3B0F7A] active:bg-[#35106D]'
                            }`}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Question Content */}
                  <div className="flex-1 overflow-y-auto">
                    <Question question={currentQuestion} />
                  </div>
                </div>
              </div>
            </Panel>
            <div className="flex items-center">
              <PanelResizeHandle className="w-1 h-[70%] rounded-full cursor-col-resize bg-[#DCD2F2] dark:bg-gray-700" />
            </div>
            {/* Right Panel (Code + IO) */}
            <Panel defaultSize={65}>
              <PanelGroup direction="vertical">
                {/* Code Editor */}
                <Panel defaultSize={60} minSize={30}>
                  <div className="h-full px-2 py-1">
                    <div
                      ref={codeEditorRef}
                      className="h-full rounded-lg border border-[#E6E0F3] bg-white/75 dark:border-slate-700 dark:bg-slate-800/90 shadow-sm px-3 py-2 flex flex-col"
                    >
                      <div className="flex justify-between items-center mb-1">
                        <h4 className="text-sm font-semibold">Code</h4>
                        <LanguageSelector
                          language={language}
                          onChange={handleLanguageChange}
                          disabled={timeUp || (currentQuestion && currentQuestion.language)}
                        />
                      </div>

                      {/* Show file tabs only for UI mode */}
                      {isUIMode && (
                        <FileTabs
                          files={virtualFS.files}
                          activeFile={virtualFS.activeFile}
                          onActiveFileChange={virtualFS.setActiveFile}
                          isDarkMode={darkTheme}
                        />
                      )}

                      <CodeEditor
                        language={isUIMode ? 'javascript' : language}
                        code={isUIMode ? virtualFS.getFileContent(virtualFS.activeFile) : currentCode}
                        setCode={(value) =>
                          isUIMode
                            ? virtualFS.updateFile(virtualFS.activeFile, value)
                            : setCodeByQuestion((prev) => ({
                                ...prev,
                                [questionKey]: value,
                              }))
                        }
                        darkTheme={darkTheme}
                        editable={!timeUp}
                        isUIMode={isUIMode}
                        onCodeChange={(newContent) => {
                          if (isUIMode) {
                            virtualFS.updateFile(virtualFS.activeFile, newContent);
                          }
                        }}
                      />
                    </div>
                  </div>
                </Panel>
                <div className="flex justify-center">
                  <PanelResizeHandle className="h-1 w-[80%] rounded-full cursor-row-resize bg-[#DCD2F2] dark:bg-gray-700" />
                </div>
                {/* Input / Output */}
                <Panel defaultSize={40} minSize={20}>
                  <div className="h-full px-2 py-1">
                    <InputOutput
                      input={input}
                      setInput={setInput}
                      output={output}
                      isDarkMode={darkTheme}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      disabled={timeUp}
                      language={language}
                      previewFiles={isUIMode ? virtualFS.files : {}}
                      isPreviewLoading={isPreviewLoading}
                    />
                  </div>
                </Panel>
              </PanelGroup>
            </Panel>
          </PanelGroup>
        </div>

        {/* <Footer /> */}
      </div>

      {showConfirmPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-semibold mb-2">
              Unsubmitted Answer
            </h3>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              You are moving away from this question without submitting.
              Please remember to submit all answers.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConfirmPopup(false);
                  setPendingNavigation(null);
                }}
                className="px-4 py-1 rounded bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-700"
              >
                Stay
              </button>

              <button
                onClick={() => {
                  setShowConfirmPopup(false);

                  if (pendingNavigation === "next") {
                    onQuestionIndexChange(currentQuestionIndex + 1);
                  } else if (pendingNavigation === "prev") {
                    onQuestionIndexChange(currentQuestionIndex - 1);
                  }

                  setPendingNavigation(null);
                }}
                className="px-4 py-1 rounded bg-[#4B1498] text-white hover:bg-[#3B0F7A]"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default CodingApp;
